export const clampNumber = (value, fallback, min, max) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  if (Number.isFinite(min) && n < min) return min;
  if (Number.isFinite(max) && n > max) return max;
  return n;
};

export const normalizeRgba = (value, fallback) => {
  const v = value && typeof value === 'object' ? value : null;
  return {
    r: clampNumber(v?.r, fallback.r, 0, 255),
    g: clampNumber(v?.g, fallback.g, 0, 255),
    b: clampNumber(v?.b, fallback.b, 0, 255),
    a: clampNumber(v?.a, fallback.a, 0, 1),
  };
};

export const rgbaToCss = (rgba, fallback = { r: 0, g: 0, b: 0, a: 1 }) => {
  const c = normalizeRgba(rgba, fallback);
  return `rgba(${Math.round(c.r)}, ${Math.round(c.g)}, ${Math.round(c.b)}, ${c.a})`;
};

export const rgbToHex = (r, g, b) => {
  const clamp = (n) => {
    const v = Math.max(0, Math.min(255, Math.round(Number(n) || 0)));
    return v.toString(16).padStart(2, '0');
  };
  return `#${clamp(r)}${clamp(g)}${clamp(b)}`;
};

export const hexToRgb = (hex) => {
  const raw = String(hex || '').trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return null;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return { r, g, b };
};

export const isEditorUiTarget = (target) => {
  if (!target || typeof target !== 'object') return false;
  const el = target instanceof Element ? target : null;
  if (!el) return false;
  return Boolean(el.closest('[data-style-editor-ui="true"]'));
};

export const getEditableElementAtPoint = ({ root, x, y, ignoreSelector }) => {
  if (!root) return null;
  const elRaw = document.elementFromPoint(x, y);
  const el =
    elRaw && elRaw instanceof SVGElement
      ? elRaw.closest('*')?.parentElement || elRaw.parentElement || elRaw
      : elRaw;
  if (!el) return null;
  if (ignoreSelector && el.closest(ignoreSelector)) return null;

  let cur = el;
  while (cur && cur !== root) {
    if (cur.getAttribute('data-style-editor-ignore') === 'true') {
      cur = cur.parentElement;
      continue;
    }
    return cur;
  }
  return root;
};

const escapeAttrValue = (value) => String(value ?? '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const getCandidateDataAttrs = (el, maxCount) => {
  if (!(el instanceof Element)) return [];
  const out = [];
  for (const attr of Array.from(el.attributes || [])) {
    const name = String(attr?.name || '');
    if (!name.startsWith('data-')) continue;
    if (name === 'data-testid') continue;
    if (name.startsWith('data-style-editor-')) continue;
    const value = attr?.value ?? '';
    if (value === '' || value == null) {
      out.push({ name, value: '' });
    } else {
      const v = String(value);
      if (v.length > 120) continue;
      if (/\d/.test(v)) continue;
      out.push({ name, value: v });
    }
    if (out.length >= maxCount) break;
  }
  return out;
};

const getCandidateClassTokens = (el, maxCount) => {
  if (!(el instanceof Element)) return [];
  const raw = Array.from(el.classList || []);
  const filtered = raw
    .map((t) => String(t || '').trim())
    .filter((t) => t && t.length >= 2 && t.length <= 64)
    .filter((t) => !t.startsWith('css-'))
    .filter((t) => !t.startsWith('data-'));
  filtered.sort((a, b) => b.length - a.length);
  const out = [];
  for (const t of filtered) {
    if (out.length >= maxCount) break;
    if (!out.includes(t)) out.push(t);
  }
  return out;
};

const buildElementSignature = (el, opts) => {
  const options = opts && typeof opts === 'object' ? opts : {};
  const tag = el instanceof Element ? el.tagName.toLowerCase() : '';
  const dataAttrs = getCandidateDataAttrs(el, options.maxDataAttrs ?? 2);
  const classTokens = getCandidateClassTokens(el, options.maxClasses ?? 3);
  return { tag, dataAttrs, classTokens };
};

const signatureHasIdentity = (sig) => Boolean(sig?.tag && ((sig?.dataAttrs?.length ?? 0) > 0 || (sig?.classTokens?.length ?? 0) > 0));

const buildSelectorPartFromSignature = (sig) => {
  if (!sig || typeof sig !== 'object') return null;
  const tag = typeof sig.tag === 'string' && sig.tag ? sig.tag : null;
  if (!tag) return null;
  const parts = [tag];
  const dataAttrs = Array.isArray(sig.dataAttrs) ? sig.dataAttrs : [];
  for (const a of dataAttrs) {
    const name = typeof a?.name === 'string' ? a.name : '';
    if (!name) continue;
    const v = a?.value ?? '';
    if (v === '' || v == null) parts.push(`[${name}]`);
    else parts.push(`[${name}="${escapeAttrValue(v)}"]`);
  }
  const classTokens = Array.isArray(sig.classTokens) ? sig.classTokens : [];
  for (const t of classTokens) parts.push(`[class~="${escapeAttrValue(t)}"]`);
  return parts.join('');
};

const elementMatchesSignature = (el, sig) => {
  if (!(el instanceof Element)) return false;
  if (!sig || typeof sig !== 'object') return false;
  if (sig.tag && el.tagName.toLowerCase() !== sig.tag) return false;
  const dataAttrs = Array.isArray(sig.dataAttrs) ? sig.dataAttrs : [];
  for (const a of dataAttrs) {
    const name = typeof a?.name === 'string' ? a.name : '';
    if (!name) continue;
    if (!el.hasAttribute(name)) return false;
    const expected = a?.value ?? '';
    if (expected !== '' && expected != null) {
      if (String(el.getAttribute(name) ?? '') !== String(expected)) return false;
    }
  }
  const classTokens = Array.isArray(sig.classTokens) ? sig.classTokens : [];
  for (const t of classTokens) {
    if (!el.classList.contains(t)) return false;
  }
  return true;
};

const getPathChildWithin = (ancestor, node) => {
  if (!(ancestor instanceof Element) || !(node instanceof Element)) return null;
  if (!ancestor.contains(node)) return null;
  let cur = node;
  while (cur && cur.parentElement && cur.parentElement !== ancestor) cur = cur.parentElement;
  return cur?.parentElement === ancestor ? cur : null;
};

const getDescendantChain = (root, leaf) => {
  const out = [];
  if (!(root instanceof Element) || !(leaf instanceof Element)) return out;
  if (!root.contains(leaf)) return out;
  let cur = leaf;
  while (cur && cur !== root) {
    out.unshift(cur);
    cur = cur.parentElement;
  }
  return out;
};

const buildRelativeSelector = (from, to) => {
  if (!(from instanceof Element) || !(to instanceof Element)) return null;
  if (from === to) return '';
  const chain = getDescendantChain(from, to);
  if (chain.length === 0) return null;
  const tail = chain.slice(-2);
  const parts = [];
  for (const el of tail) {
    const sig = buildElementSignature(el, { maxDataAttrs: 2, maxClasses: 2 });
    if (!signatureHasIdentity(sig)) return null;
    const part = buildSelectorPartFromSignature(sig);
    if (!part) return null;
    parts.push(part);
  }
  return parts.join(' ');
};

const buildSimilarSelector = (element, effectiveRoot, rootSelector) => {
  if (!(element instanceof Element) || !(effectiveRoot instanceof Element)) return null;
  const selfSig = buildElementSignature(element, { maxDataAttrs: 2, maxClasses: 3 });
  if (!signatureHasIdentity(selfSig)) return null;

  let cur = element;
  for (let depth = 0; depth < 7; depth += 1) {
    const container = cur.parentElement;
    if (!container) break;
    if (!effectiveRoot.contains(container)) break;

    const itemEl = getPathChildWithin(container, element);
    if (!itemEl) {
      cur = container;
      continue;
    }

    const itemSig = buildElementSignature(itemEl, { maxDataAttrs: 2, maxClasses: 3 });
    if (!signatureHasIdentity(itemSig)) {
      cur = container;
      continue;
    }

    let similarCount = 0;
    for (const child of Array.from(container.children || [])) {
      if (elementMatchesSignature(child, itemSig)) similarCount += 1;
      if (similarCount >= 2) break;
    }
    if (similarCount < 2) {
      cur = container;
      continue;
    }

    const containerSig = buildElementSignature(container, { maxDataAttrs: 2, maxClasses: 2 });
    if (!signatureHasIdentity(containerSig)) {
      cur = container;
      continue;
    }
    const containerPart = buildSelectorPartFromSignature(containerSig);
    const itemPart = buildSelectorPartFromSignature(itemSig);
    if (!containerPart || !itemPart) {
      cur = container;
      continue;
    }
    const relative = buildRelativeSelector(itemEl, element);
    const full = `${rootSelector} ${containerPart} > ${itemPart}${relative ? ` ${relative}` : ''}`;
    try {
      const count = typeof document === 'undefined' ? 0 : document.querySelectorAll(full).length;
      if (count >= 2) return full;
    } catch {}
    cur = container;
  }

  const selfPart = buildSelectorPartFromSignature(selfSig);
  if (!selfPart) return null;
  const global = `${rootSelector} ${selfPart}`;
  try {
    const count = typeof document === 'undefined' ? 0 : document.querySelectorAll(global).length;
    if (count >= 2) return global;
  } catch {}
  return null;
};

export const buildDomPathSelector = (element, root) => {
  if (!element || !root) return null;
  if (!(element instanceof Element) || !(root instanceof Element)) return null;
  let effectiveRoot = root;
  if (!effectiveRoot.contains(element)) {
    const body = typeof document !== 'undefined' ? document.body : null;
    if (body && body instanceof Element && body.contains(element)) {
      effectiveRoot = body;
    } else {
      return null;
    }
  }

  const isBodyRoot = typeof document !== 'undefined' && effectiveRoot === document.body;
  const rootSelector = isBodyRoot
    ? 'body'
    : effectiveRoot.id
      ? `#${CSS.escape(effectiveRoot.id)}`
      : `[data-style-editor-root="true"]`;
  if (element === effectiveRoot) return rootSelector;

  const groupEl = element.closest('[data-style-group]');
  if (groupEl && effectiveRoot.contains(groupEl)) {
    const group = groupEl.getAttribute('data-style-group');
    if (group && groupEl === element) return `${rootSelector} [data-style-group="${CSS.escape(group)}"]`;
  }

  const keyEl = element.closest('[data-style-key]');
  if (keyEl && effectiveRoot.contains(keyEl)) {
    const key = keyEl.getAttribute('data-style-key');
    if (key && keyEl === element) return `${rootSelector} [data-style-key="${CSS.escape(key)}"]`;
  }

  const similarSelector = buildSimilarSelector(element, effectiveRoot, rootSelector);
  if (similarSelector) return similarSelector;

  const parts = [];
  let cur = element;
  while (cur && cur !== effectiveRoot) {
    const parent = cur.parentElement;
    if (!parent) break;
    const tag = cur.tagName.toLowerCase();
    const sameTagSiblings = Array.from(parent.children).filter((c) => c.tagName === cur.tagName);
    const idx = Math.max(1, sameTagSiblings.indexOf(cur) + 1);
    parts.unshift(`${tag}:nth-of-type(${idx})`);
    cur = parent;
  }

  if (parts.length === 0) return rootSelector;
  return `${rootSelector} ${parts.join(' > ')}`;
};

export const sanitizeCssSelector = (selector) => {
  const s = typeof selector === 'string' ? selector.trim() : '';
  if (!s) return null;
  if (s.length > 2000) return null;
  if (s.includes('{') || s.includes('}') || s.includes('<')) return null;
  return s;
};

export const sanitizeFontFamilyKey = (key) => {
  const raw = typeof key === 'string' ? key.trim() : '';
  if (!raw) return null;
  if (raw.length > 120) return null;
  if (raw.includes(';') || raw.includes('{') || raw.includes('}')) return null;
  return raw;
};

export const getStyleBlockId = (element, root) => {
  const el = element instanceof Element ? element : null;
  const stop = root instanceof Element ? root : null;
  if (!el) return null;
  let cur = el;
  while (cur) {
    const v = cur.getAttribute('data-style-block');
    if (v) return String(v);
    if (stop && cur === stop) break;
    cur = cur.parentElement;
  }
  return null;
};

export const buildOverridesCss = (overrides, resolveFontFamilyKey) => {
  if (!overrides || typeof overrides !== 'object') return '';
  const rules = [];

  for (const [selectorRaw, styleRaw] of Object.entries(overrides)) {
    const selector = sanitizeCssSelector(selectorRaw);
    if (!selector) continue;
    if (!styleRaw || typeof styleRaw !== 'object') continue;

    const decl = [];
    if (styleRaw?.background_color) decl.push(`background-color: ${rgbaToCss(styleRaw.background_color)} !important;`);
    if (styleRaw?.text_color)
      decl.push(`color: ${rgbaToCss(styleRaw.text_color, { r: 255, g: 255, b: 255, a: 1 })} !important;`);
    const ffKey = sanitizeFontFamilyKey(styleRaw?.font_family);
    if (ffKey && typeof resolveFontFamilyKey === 'function') decl.push(`font-family: ${resolveFontFamilyKey(ffKey)} !important;`);
    const fontSize = Number(styleRaw?.font_size);
    if (Number.isFinite(fontSize) && fontSize > 0) decl.push(`font-size: ${fontSize}px !important;`);
    const letterSpacing = Number(styleRaw?.letter_spacing);
    if (Number.isFinite(letterSpacing)) decl.push(`letter-spacing: ${letterSpacing}px !important;`);
    const fontWeight = Number(styleRaw?.font_weight);
    if (Number.isFinite(fontWeight) && fontWeight > 0) decl.push(`font-weight: ${fontWeight} !important;`);
    const align =
      styleRaw?.text_align === 'left' || styleRaw?.text_align === 'center' || styleRaw?.text_align === 'right' ? styleRaw.text_align : null;
    if (align) decl.push(`text-align: ${align} !important;`);

    const width = Number(styleRaw?.width);
    if (Number.isFinite(width) && width >= 0) decl.push(`width: ${width}px !important;`);
    const height = Number(styleRaw?.height);
    if (Number.isFinite(height) && height >= 0) decl.push(`height: ${height}px !important;`);

    const marginTop = Number(styleRaw?.margin_top);
    if (Number.isFinite(marginTop)) decl.push(`margin-top: ${marginTop}px !important;`);
    const marginRight = Number(styleRaw?.margin_right);
    if (Number.isFinite(marginRight)) decl.push(`margin-right: ${marginRight}px !important;`);
    const marginBottom = Number(styleRaw?.margin_bottom);
    if (Number.isFinite(marginBottom)) decl.push(`margin-bottom: ${marginBottom}px !important;`);
    const marginLeft = Number(styleRaw?.margin_left);
    if (Number.isFinite(marginLeft)) decl.push(`margin-left: ${marginLeft}px !important;`);

    const paddingTop = Number(styleRaw?.padding_top);
    if (Number.isFinite(paddingTop)) decl.push(`padding-top: ${paddingTop}px !important;`);
    const paddingRight = Number(styleRaw?.padding_right);
    if (Number.isFinite(paddingRight)) decl.push(`padding-right: ${paddingRight}px !important;`);
    const paddingBottom = Number(styleRaw?.padding_bottom);
    if (Number.isFinite(paddingBottom)) decl.push(`padding-bottom: ${paddingBottom}px !important;`);
    const paddingLeft = Number(styleRaw?.padding_left);
    if (Number.isFinite(paddingLeft)) decl.push(`padding-left: ${paddingLeft}px !important;`);

    if (decl.length === 0) continue;
    rules.push(`${selector}{${decl.join('')}}`);
  }

  return rules.join('\n');
};
